import React, { useEffect, useMemo, useState } from 'react'
import {
	Box,
	Title,
	Text,
	Button,
	Group,
	TextInput,
	Badge,
	Stack,
	SegmentedControl,
	FileInput,
	ColorInput,
	Tabs,
	Checkbox,
} from '@mantine/core'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	addMessageSetup,
	getExperience,
	getMessageSetups,
	updateMessageSetup,
} from '../services/actions/experienceActions'
import {
	sendTestEmail,
	sendTestSms,
} from '../services/actions/messagingActions'
import {
	EMAIL_SEPARATOR_TOKEN,
	DEFAULT_SEPARATOR_COLOR,
	appendPassLinkToSmsBody,
	buildReceiptSummaryHtml,
	buildStandardEmailHtml,
	getStockTemplate,
	findSetupForTemplate,
	getTemplateEditorDefaults,
	getSupportedDeliveryMethods,
	isCheckInPassTemplate,
	normalizeDeliveryMethod,
	renderEmailBodyHtml,
	buildVariableOptions,
	buildPassPreviewUrl,
} from '../constants/stockTemplates'
import {
	uploadImageToS3,
	uploadMessagingMediaToS3,
} from '../services/utils/uploadImageToS3'
import { toast } from '../services/utils/toast'
import ImageCropperModal from '../components/ImageCropperModal'
import { readFileAsDataUrl } from '../services/utils/imageCropper'
import VariableTriggerTextarea from '../components/VariableTriggerTextarea'
import useUnsavedChangesWarning from '../hooks/useUnsavedChangesWarning'

const replaceAll = (value, needle, replacement) =>
	String(value || '')
		.split(needle)
		.join(replacement)

const getNonEmptyTrimmedValue = (...values) => {
	for (const value of values) {
		if (typeof value !== 'string') continue
		const normalized = value.trim()
		if (normalized) return normalized
	}
	return null
}

const applyTemplateVariables = ({
	value = '',
	travelerName = '',
	groupLeaderName = '',
	groupLeaderPhone = '',
	groupMemberNames = '',
	experienceName = '',
	orgName = '',
	clickHereToViewPass = '',
	orderNumber = '',
	purchaseDate = '',
	purchasedItems = '',
	orderSubtotal = '',
	orderFee = '',
	orderTotal = '',
}) => {
	let output = String(value || '')
	output = replaceAll(output, '{{travelerName}}', travelerName || '')
	output = replaceAll(output, '{{groupLeaderName}}', groupLeaderName || '')
	output = replaceAll(output, '{{groupLeaderPhone}}', groupLeaderPhone || '')
	output = replaceAll(output, '{{groupMemberNames}}', groupMemberNames || '')
	output = replaceAll(output, '{{experienceName}}', experienceName || '')
	output = replaceAll(output, '{{orgName}}', orgName || '')
	output = replaceAll(output, '{{orderNumber}}', orderNumber || '')
	output = replaceAll(output, '{{purchaseDate}}', purchaseDate || '')
	output = replaceAll(output, '{{purchasedItems}}', purchasedItems || '')
	output = replaceAll(output, '{{orderSubtotal}}', orderSubtotal || '')
	output = replaceAll(output, '{{orderFee}}', orderFee || '')
	output = replaceAll(output, '{{orderTotal}}', orderTotal || '')
	output = replaceAll(
		output,
		'{{clickHereToViewPass}}',
		clickHereToViewPass || '',
	)
	return output
}

const HEADER_IMAGE_TARGET_WIDTH = 1200
const HEADER_IMAGE_TARGET_HEIGHT = 420
const DELIVERY_METHOD_LABELS = {
	email: 'Email',
	sms: 'SMS',
	both: 'Both',
}
const FAKE_RECEIPT = {
	orderNumber: '#LH-4821',
	purchaseDate: 'August 28, 2026',
	items: [
		{ name: 'Experience Weekend T-Shirt', quantity: 1, amount: '$32.00' },
		{ name: 'Commemorative Pin', quantity: 2, amount: '$16.00' },
	],
	subtotal: '$48.00',
	fee: '$2.40',
	total: '$50.40',
	showFee: true,
}

const serializeTemplateDraft = ({
	deliveryMethod,
	emailSubject,
	emailBody,
	smsBody,
	smsMediaUrl,
	appendPassLinkToSms,
	clickText,
	headerImageUrl,
	headerText,
	separatorColor,
}) =>
	JSON.stringify({
		deliveryMethod,
		emailSubject,
		emailBody,
		smsBody,
		smsMediaUrl,
		appendPassLinkToSms,
		clickText,
		headerImageUrl,
		headerText,
		separatorColor,
	})

const EditExperienceStockTemplatePage = () => {
	const { orgSlug, experienceSlug, templateKey } = useParams()
	const navigate = useNavigate()
	const user = useSelector((state) => state.user?.authUser)
	const experience = useSelector((state) => state.experience?.experienceDetails)
	const currentExperience = useSelector(
		(state) => state.user?.currentExperience,
	)
	const currentOrg = useSelector((state) => state.user?.currentOrg)
	const [previewExperience, setPreviewExperience] = useState(null)
	const resolvedExperienceName =
		getNonEmptyTrimmedValue(
			previewExperience?.shortName,
			experience?.shortName,
			currentExperience?.shortName,
			previewExperience?.name,
			experience?.name,
			currentExperience?.name,
			experienceSlug,
		) || 'Experience'

	const template = useMemo(() => getStockTemplate(templateKey), [templateKey])
	const isEventDriven = template?.type === 'event_driven'
	const isCheckInPass = isCheckInPassTemplate(template)
	const isReceipt = template?.hasReceiptSummary === true
	const useExperiencePage =
		previewExperience?.useExperiencePage ??
		experience?.useExperiencePage ??
		currentExperience?.useExperiencePage ??
		true
	const previewExperienceId =
		previewExperience?.id || experience?.id || currentExperience?.id || ''
	const supportedDeliveryMethods = useMemo(
		() => getSupportedDeliveryMethods(template),
		[template],
	)
	const supportsDeliveryMethodChoice = supportedDeliveryMethods.length > 1
	const deliveryMethodOptions = useMemo(
		() =>
			supportedDeliveryMethods.map((method) => ({
				label: DELIVERY_METHOD_LABELS[method] || method,
				value: method,
			})),
		[supportedDeliveryMethods],
	)

	const [existingSetup, setExistingSetup] = useState(null)
	const [deliveryMethod, setDeliveryMethod] = useState('email')
	const [emailSubject, setEmailSubject] = useState('')
	const [emailBody, setEmailBody] = useState('')
	const [smsBody, setSmsBody] = useState('')
	const [smsMediaUrl, setSmsMediaUrl] = useState('')
	const [appendPassLinkToSms, setAppendPassLinkToSms] = useState(true)
	const [clickText, setClickText] = useState('CLICK HERE')
	const [headerImageUrl, setHeaderImageUrl] = useState('')
	const [headerText, setHeaderText] = useState('')
	const [separatorColor, setSeparatorColor] = useState(DEFAULT_SEPARATOR_COLOR)
	const [activeEditorTab, setActiveEditorTab] = useState('email')
	const [cropModalOpened, setCropModalOpened] = useState(false)
	const [cropSourceUrl, setCropSourceUrl] = useState('')
	const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false)
	const [uploadingSmsMedia, setUploadingSmsMedia] = useState(false)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [sendingTestSms, setSendingTestSms] = useState(false)
	const [saveStatus, setSaveStatus] = useState('idle')
	const [lastSavedAt, setLastSavedAt] = useState(null)
	const [savedDraft, setSavedDraft] = useState(null)

	const currentDraft = useMemo(
		() =>
			serializeTemplateDraft({
				deliveryMethod,
				emailSubject,
				emailBody,
				smsBody,
				smsMediaUrl,
				appendPassLinkToSms,
				clickText,
				headerImageUrl,
				headerText,
				separatorColor,
			}),
		[
			deliveryMethod,
			emailSubject,
			emailBody,
			smsBody,
			smsMediaUrl,
			appendPassLinkToSms,
			clickText,
			headerImageUrl,
			headerText,
			separatorColor,
		],
	)
	const hasUnsavedChanges =
		!loading && savedDraft !== null && currentDraft !== savedDraft

	useUnsavedChangesWarning(hasUnsavedChanges)

	useEffect(() => {
		let isCancelled = false

		const loadPreviewExperience = async () => {
			if (!experienceSlug || !orgSlug) return
			try {
				const latestExperience = await getExperience(experienceSlug, orgSlug)
				if (!isCancelled) setPreviewExperience(latestExperience || null)
			} catch (err) {
				console.error('Error loading preview experience details', err)
			}
		}

		loadPreviewExperience()
		return () => {
			isCancelled = true
		}
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		const loadSetup = async () => {
			if (!template) return
			try {
				setLoading(true)
				setSavedDraft(null)
				const setups = await getMessageSetups(experienceSlug, orgSlug)
				const match = findSetupForTemplate(setups, template)
				const defaults = getTemplateEditorDefaults(template, match)
				const loadedDeliveryMethod = normalizeDeliveryMethod(
					match?.deliveryMethod || template?.deliveryMethod || 'email',
				)

				setExistingSetup(match || null)
				setDeliveryMethod(loadedDeliveryMethod)
				setEmailSubject(defaults.emailSubject)
				setEmailBody(defaults.emailBody)
				setSmsBody(defaults.smsBody)
				setSmsMediaUrl(defaults.smsMediaUrl)
				setAppendPassLinkToSms(defaults.appendPassLinkToSms)
				setClickText(defaults.clickText)
				setHeaderImageUrl(defaults.headerImageUrl)
				setHeaderText(defaults.headerText)
				setSeparatorColor(defaults.separatorColor)
				setSavedDraft(
					serializeTemplateDraft({
						deliveryMethod: loadedDeliveryMethod,
						emailSubject: defaults.emailSubject,
						emailBody: defaults.emailBody,
						smsBody: defaults.smsBody,
						smsMediaUrl: defaults.smsMediaUrl,
						appendPassLinkToSms: defaults.appendPassLinkToSms,
						clickText: defaults.clickText,
						headerImageUrl: defaults.headerImageUrl,
						headerText: defaults.headerText,
						separatorColor: defaults.separatorColor,
					}),
				)
			} catch (err) {
				console.error('Error loading stock template setup', err)
				alert(err.message || 'Failed to load template setup')
			} finally {
				setLoading(false)
			}
		}

		loadSetup()
	}, [template, orgSlug, experienceSlug])

	useEffect(() => {
		if (deliveryMethod === 'sms') {
			setActiveEditorTab('sms')
			return
		}
		setActiveEditorTab('email')
	}, [deliveryMethod])

	const previewContext = useMemo(
		() => ({
			to: `${user?.name || 'Demo User'} <${user?.email || 'demo@example.com'}>`,
			smsTo: user?.phone || 'No phone number on your user profile',
			travelerName: 'John Doe',
			groupLeaderName: 'Jane Doe',
			groupLeaderPhone: '+1 (555) 123-4567',
			groupMemberNames: 'Jeff Gordon, Richard Petty, Chase Elliott',
			experienceName: resolvedExperienceName,
			orgName: currentOrg?.orgName || 'LiveHQ',
			clickHereToViewPass: buildPassPreviewUrl(
				useExperiencePage,
				previewExperienceId,
			),
			orderNumber: FAKE_RECEIPT.orderNumber,
			purchaseDate: FAKE_RECEIPT.purchaseDate,
			purchasedItems: FAKE_RECEIPT.items
				.map((item) => `${item.quantity > 1 ? `${item.quantity}× ` : ''}${item.name}`)
				.join(', '),
			orderSubtotal: FAKE_RECEIPT.subtotal,
			orderFee: FAKE_RECEIPT.fee,
			orderTotal: FAKE_RECEIPT.total,
		}),
		[
			user,
			resolvedExperienceName,
			currentOrg,
			useExperiencePage,
			previewExperienceId,
		],
	)

	const renderedSubject = useMemo(() => {
		return applyTemplateVariables({
			value: emailSubject || template?.defaultSubject || '',
			travelerName: previewContext.travelerName,
			groupLeaderName: previewContext.groupLeaderName,
			groupLeaderPhone: previewContext.groupLeaderPhone,
			groupMemberNames: previewContext.groupMemberNames,
			experienceName: previewContext.experienceName,
			orgName: previewContext.orgName,
			clickHereToViewPass: previewContext.clickHereToViewPass,
			orderNumber: previewContext.orderNumber,
			purchaseDate: previewContext.purchaseDate,
			purchasedItems: previewContext.purchasedItems,
			orderSubtotal: previewContext.orderSubtotal,
			orderFee: previewContext.orderFee,
			orderTotal: previewContext.orderTotal,
		})
	}, [emailSubject, template, previewContext])

	const renderedHeaderText = useMemo(
		() =>
			applyTemplateVariables({
				value: headerText,
				travelerName: previewContext.travelerName,
				groupLeaderName: previewContext.groupLeaderName,
				groupLeaderPhone: previewContext.groupLeaderPhone,
				groupMemberNames: previewContext.groupMemberNames,
				experienceName: previewContext.experienceName,
				orgName: previewContext.orgName,
				clickHereToViewPass: previewContext.clickHereToViewPass,
				orderNumber: previewContext.orderNumber,
				purchaseDate: previewContext.purchaseDate,
				purchasedItems: previewContext.purchasedItems,
				orderSubtotal: previewContext.orderSubtotal,
				orderFee: previewContext.orderFee,
				orderTotal: previewContext.orderTotal,
			}),
		[headerText, previewContext],
	)

	const renderedEmailBody = useMemo(
		() =>
			renderEmailBodyHtml({
				template,
				emailBody,
				clickText,
				clickUrl: previewContext.clickHereToViewPass,
				travelerName: previewContext.travelerName,
				groupLeaderName: previewContext.groupLeaderName,
				groupLeaderPhone: previewContext.groupLeaderPhone,
				groupMemberNames: previewContext.groupMemberNames,
				experienceName: previewContext.experienceName,
				orgName: previewContext.orgName,
				orderNumber: previewContext.orderNumber,
				purchaseDate: previewContext.purchaseDate,
				purchasedItems: previewContext.purchasedItems,
				orderSubtotal: previewContext.orderSubtotal,
				orderFee: previewContext.orderFee,
				orderTotal: previewContext.orderTotal,
				separatorColor,
			}),
		[template, emailBody, clickText, previewContext, separatorColor],
	)

	const renderedEmailHtml = useMemo(
		() =>
			buildStandardEmailHtml({
				headerImageUrl,
				headerText: renderedHeaderText,
				bodyHtml: `${renderedEmailBody}${
					isReceipt ? buildReceiptSummaryHtml(FAKE_RECEIPT) : ''
				}`,
				separatorColor,
			}),
		[
			headerImageUrl,
			renderedHeaderText,
			renderedEmailBody,
			separatorColor,
			isReceipt,
		],
	)

	const renderedSmsBody = useMemo(() => {
		const body = applyTemplateVariables({
			value: smsBody,
			travelerName: previewContext.travelerName,
			groupLeaderName: previewContext.groupLeaderName,
			groupLeaderPhone: previewContext.groupLeaderPhone,
			groupMemberNames: previewContext.groupMemberNames,
			experienceName: previewContext.experienceName,
			orgName: previewContext.orgName,
			clickHereToViewPass: previewContext.clickHereToViewPass,
			orderNumber: previewContext.orderNumber,
			purchaseDate: previewContext.purchaseDate,
			purchasedItems: previewContext.purchasedItems,
			orderSubtotal: previewContext.orderSubtotal,
			orderFee: previewContext.orderFee,
			orderTotal: previewContext.orderTotal,
		})
		if (!isCheckInPass || !appendPassLinkToSms) return body
		return appendPassLinkToSmsBody(body, previewContext.clickHereToViewPass)
	}, [smsBody, previewContext, isCheckInPass, appendPassLinkToSms])

	const isEmailEnabled = deliveryMethod !== 'sms'
	const isSmsEnabled = deliveryMethod === 'sms' || deliveryMethod === 'both'
	const showTabbedEditors =
		supportsDeliveryMethodChoice && deliveryMethod === 'both'
	const showEmailTestButton =
		isEmailEnabled && (!showTabbedEditors || activeEditorTab === 'email')
	const showSmsTestButton =
		isSmsEnabled && (!showTabbedEditors || activeEditorTab === 'sms')
	const variableOptions = useMemo(
		() =>
			buildVariableOptions(template?.variables || [], { useExperiencePage }),
		[template, useExperiencePage],
	)

	const handleInsertSeparatorInEmail = () => {
		setEmailBody(
			(prev) => `${prev}${prev ? '\n' : ''}${EMAIL_SEPARATOR_TOKEN}\n`,
		)
	}

	const handleHeaderImageSelect = async (file) => {
		if (!file) return
		try {
			const sourceUrl = await readFileAsDataUrl(file)
			setCropSourceUrl(sourceUrl)
			setCropModalOpened(true)
		} catch (err) {
			alert(err.message || 'Failed to load image for cropping')
		}
	}

	const handleOpenHeaderImageRecrop = () => {
		if (!headerImageUrl) return
		setCropSourceUrl(headerImageUrl)
		setCropModalOpened(true)
	}

	const handleUploadCroppedHeaderImage = async (croppedBlob) => {
		if (!croppedBlob) return
		try {
			setUploadingHeaderImage(true)
			const croppedFile = new File(
				[croppedBlob],
				`template-header-${Date.now()}.jpg`,
				{ type: 'image/jpeg' },
			)
			const uploadedUrl = await uploadImageToS3(croppedFile)
			setHeaderImageUrl(uploadedUrl)
			setCropModalOpened(false)
		} catch (err) {
			console.error('Error uploading cropped header image', err)
			alert(err.message || 'Failed to upload header image')
		} finally {
			setUploadingHeaderImage(false)
		}
	}

	const handleSmsMediaSelect = async (file) => {
		if (!file) return
		try {
			setUploadingSmsMedia(true)
			const uploadedUrl = await uploadMessagingMediaToS3(file, orgSlug)
			setSmsMediaUrl(uploadedUrl)
		} catch (err) {
			console.error('Error uploading MMS image', err)
			alert(err.message || 'Failed to upload MMS image')
		} finally {
			setUploadingSmsMedia(false)
		}
	}

	const handleSaveTemplate = async () => {
		if (!template) return
		if (template.hasClickText && !clickText.trim()) {
			alert('Pass link text cannot be blank.')
			return
		}
		const hasEmailContent = isEventDriven
			? !!emailBody.trim()
			: !!(emailSubject.trim() || emailBody.trim())
		const hasSmsContent = !!smsBody.trim()
		if (isEmailEnabled && !hasEmailContent) {
			alert(
				isEventDriven
					? 'Email body is required for email delivery.'
					: 'Email subject or email body is required for email delivery.',
			)
			return
		}
		if (isSmsEnabled && !hasSmsContent) {
			alert('SMS body is required for SMS delivery.')
			return
		}

		try {
			setSaving(true)
			setSaveStatus('saving')

			const emailLayoutPayload = {
				headerImageUrl,
				headerText,
				separatorColor,
			}
			const existingSetupDataSource =
				existingSetup?.data &&
				typeof existingSetup.data === 'object' &&
				!Array.isArray(existingSetup.data)
					? existingSetup.data
					: {}
			const existingSetupData = { ...existingSetupDataSource }
			delete existingSetupData.useExperiencePage

			const dataPayload = {
				...existingSetupData,
				emailSubject,
				emailBody,
				smsBody,
				smsMediaUrl,
				...emailLayoutPayload,
				...(isEventDriven
					? {}
					: {
							subject: emailSubject,
							body: emailBody,
						}),
				...(isCheckInPass
					? { appendPassLinkToSms }
					: {}),
				...(template.hasClickText ? { clickText } : {}),
			}
			const savedDeliveryMethod = supportsDeliveryMethodChoice
				? deliveryMethod
				: template.deliveryMethod

			let saved
			if (existingSetup?.id) {
				saved = await updateMessageSetup(
					orgSlug,
					experienceSlug,
					existingSetup.id,
					{
						deliveryMethod: savedDeliveryMethod,
						data: dataPayload,
					},
				)
			} else {
				saved = await addMessageSetup(orgSlug, experienceSlug, {
					description: template.canonicalDescription,
					recipient: template.recipient,
					action: template.action,
					deliveryMethod: savedDeliveryMethod,
					data: dataPayload,
				})
			}

			setExistingSetup(saved)
			setSavedDraft(currentDraft)
			setSaveStatus('saved')
			setLastSavedAt(new Date())
			toast.success({
				title: 'Template saved',
				message: 'Your template changes were saved successfully.',
			})
		} catch (err) {
			console.error('Error saving stock template', err)
			setSaveStatus('error')
			toast.error({
				title: 'Save failed',
				message: err.message || 'Failed to save template',
			})
		} finally {
			setSaving(false)
		}
	}

	const handleTestSend = async () => {
		if (deliveryMethod === 'sms') {
			alert('Test email is not available for SMS-only templates.')
			return
		}
		if (!emailSubject && !emailBody) {
			alert('Please add template content before test sending.')
			return
		}

		try {
			await sendTestEmail(orgSlug, experienceSlug, {
				templateKey: template?.key,
			})
			alert(`Test email sent to ${user?.email || 'your email'}.`)
		} catch (err) {
			console.error('Error test-sending stock template', err)
			alert(err.message || 'Failed to send test email')
		}
	}

	const handleTestSendSms = async () => {
		if (!isSmsEnabled) {
			alert('Test SMS is only available when SMS delivery is enabled.')
			return
		}
		if (!smsBody.trim()) {
			alert('Please add SMS content before test sending.')
			return
		}

		try {
			setSendingTestSms(true)
			const response = await sendTestSms(orgSlug, experienceSlug, {
				templateKey: template?.key,
			})
			toast.success({
				title: 'Test SMS sent',
				message: `Test SMS sent${response?.to ? ` to ${response.to}` : ''}.`,
			})
		} catch (err) {
			console.error('Error test-sending stock template SMS', err)
			const errorMessage = err.message || 'Failed to send test SMS'
			const isMissingProfilePhone = errorMessage.startsWith(
				'No phone number is set on your user profile.',
			)

			if (isMissingProfilePhone) {
				const notificationId = 'test-sms-missing-profile-phone'
				toast.error({
					id: notificationId,
					title: 'Failed to send test SMS',
					autoClose: 10000,
					message: (
						<Stack spacing='xs' align='flex-start'>
							<Text size='sm'>{errorMessage}</Text>
							<Button
								size='xs'
								compact
								variant='light'
								color='red'
								onClick={() => {
									toast.hide(notificationId)
									navigate('/account/settings')
								}}
							>
								Add phone number
							</Button>
						</Stack>
					),
				})
				return
			}

			toast.error({
				title: 'Failed to send test SMS',
				message: errorMessage,
			})
		} finally {
			setSendingTestSms(false)
		}
	}

	if (!template) {
		return (
			<Box px='md' py='md'>
				<Title order={3}>Unknown Template</Title>
				<Text c='dimmed' mb='md'>
					The selected stock template does not exist.
				</Text>
				<Button
					onClick={() =>
						navigate(
							`/${orgSlug}/experiences/${experienceSlug}/manage/messagesetups`,
						)
					}
				>
					Back to Message Templates
				</Button>
			</Box>
		)
	}

	const emailEditorFields = (
		<>
			<VariableTriggerTextarea
				inputType='text'
				label='Subject'
				value={emailSubject}
				onChange={setEmailSubject}
				placeholder={template.defaultSubject}
				variableOptions={variableOptions}
			/>

			<FileInput
				label={`Header Image (${HEADER_IMAGE_TARGET_WIDTH}px by ${HEADER_IMAGE_TARGET_HEIGHT}px)`}
				placeholder='Choose header image'
				accept='image/png,image/jpeg,image/webp'
				onChange={handleHeaderImageSelect}
				description='Cropped to this standard banner size for consistent email layouts.'
			/>
			{headerImageUrl && (
				<Box>
					<Text size='xs' c='dimmed' mb={6}>
						Current Header Image (click to re-crop)
					</Text>
					<img
						src={headerImageUrl}
						alt='Header'
						onClick={handleOpenHeaderImageRecrop}
						style={{
							width: '100%',
							maxWidth: 560,
							borderRadius: 8,
							display: 'block',
							cursor: 'pointer',
						}}
					/>
					<Button
						mt='xs'
						size='xs'
						variant='subtle'
						color='red'
						onClick={() => setHeaderImageUrl('')}
					>
						Remove Header Image
					</Button>
				</Box>
			)}

			<VariableTriggerTextarea
				inputType='text'
				label='Header Text'
				value={headerText}
				onChange={setHeaderText}
				placeholder={`Welcome to ${previewContext.experienceName}`}
				variableOptions={variableOptions}
			/>

			<ColorInput
				label='Separator Color'
				value={separatorColor}
				onChange={setSeparatorColor}
				format='hex'
			/>

			<VariableTriggerTextarea
				label='Email Body'
				value={emailBody}
				onChange={setEmailBody}
				minRows={8}
				description={`Use ${EMAIL_SEPARATOR_TOKEN} on its own line to insert a colored separator.`}
				placeholder='Write your message body...'
				styles={{ input: { fontFamily: 'monospace' } }}
				variableOptions={variableOptions}
			/>

			<Group spacing='xs'>
				<Button
					size='xs'
					variant='outline'
					onClick={handleInsertSeparatorInEmail}
				>
					Insert Separator
				</Button>
			</Group>

			{template.hasClickText && (
				<TextInput
					label='Pass Link Text'
					value={clickText}
					onChange={(e) => setClickText(e.target.value || 'CLICK HERE')}
					placeholder='CLICK HERE'
				/>
			)}
		</>
	)

	const smsEditorFields = (
		<>
			<VariableTriggerTextarea
				label='SMS Body'
				value={smsBody}
				onChange={setSmsBody}
				minRows={4}
				placeholder='Write your SMS message...'
				description={
					smsMediaUrl
						? 'MMS with image'
						: `${Math.ceil((renderedSmsBody.length || 0) / 160)} SMS segment(s)`
				}
				styles={{ input: { fontFamily: 'monospace' } }}
				variableOptions={variableOptions}
			/>
			<FileInput
				label='MMS Image'
				placeholder='Choose image'
				accept='image/png,image/jpeg,image/webp'
				onChange={handleSmsMediaSelect}
				disabled={uploadingSmsMedia}
				description='JPG, PNG, or WebP up to 1MB. Uploaded at original dimensions; not cropped.'
			/>
			{uploadingSmsMedia && (
				<Text size='sm' c='dimmed'>
					Uploading image...
				</Text>
			)}
			{smsMediaUrl && (
				<Box>
					<Text size='xs' c='dimmed' mb={6}>
						Current MMS Image
					</Text>
					<img
						src={smsMediaUrl}
						alt='MMS'
						style={{
							width: '100%',
							maxWidth: 360,
							borderRadius: 8,
							display: 'block',
						}}
					/>
					<Button
						mt='xs'
						size='xs'
						variant='subtle'
						color='red'
						onClick={() => setSmsMediaUrl('')}
					>
						Remove MMS Image
					</Button>
				</Box>
			)}
			{isCheckInPass && (
				<Checkbox
					label='Append pass link to SMS message'
					checked={appendPassLinkToSms}
					onChange={(event) =>
						setAppendPassLinkToSms(event.currentTarget.checked)
					}
				/>
			)}
		</>
	)

	const emailPreview = (
		<Box>
			<Title order={5} mb='xs'>
				Email Preview
			</Title>
			<Box
				p='md'
				style={{
					border: '1px solid #ddd',
					borderRadius: '8px',
					backgroundColor: '#f9f9f9',
				}}
			>
				<Text fw={500}>To: {previewContext.to}</Text>
				<Text fw={500} mt={4}>
					Subject: {renderedSubject}
				</Text>
				<Box
					mt='xs'
					style={{
						borderRadius: '6px',
						overflow: 'hidden',
						border: '1px solid #d2d6dc',
						backgroundColor: '#ffffff',
					}}
					dangerouslySetInnerHTML={{ __html: renderedEmailHtml }}
				/>
			</Box>
		</Box>
	)

	const smsPreview = (
		<Box>
			<Title order={5} mb='xs'>
				Message Preview
			</Title>
			<Box
				p='md'
				style={{
					border: '1px solid #ddd',
					borderRadius: '8px',
					backgroundColor: '#f9f9f9',
					whiteSpace: 'pre-wrap',
				}}
			>
				<Text fw={500}>To: {previewContext.smsTo}</Text>
				<Text mt='xs'>{renderedSmsBody || '(empty message)'}</Text>
				{smsMediaUrl && (
					<Box mt='sm'>
						<img
							src={smsMediaUrl}
							alt='MMS preview'
							style={{
								width: '100%',
								maxWidth: 320,
								borderRadius: 8,
								display: 'block',
							}}
						/>
					</Box>
				)}
			</Box>
		</Box>
	)

	return (
		<Box px='md' py='md'>
			<Group position='apart' align='flex-start' mb='md'>
				<Box>
					<Title order={2}>{template.name}</Title>
					<Text c='dimmed' size='sm'>
						{template.description}
					</Text>
				</Box>
				<Badge
					color={template.type === 'on_demand' ? 'blue' : 'orange'}
					variant='light'
				>
					{template.type}
				</Badge>
			</Group>

			<Group spacing='xs' mb='md'>
				<Button
					variant='subtle'
					onClick={() =>
						navigate(
							`/${orgSlug}/experiences/${experienceSlug}/manage/messagesetups`,
						)
					}
				>
					Back to Message Templates
				</Button>
			</Group>

			{loading ? (
				<Text c='dimmed'>Loading template...</Text>
			) : (
				<Stack spacing='md' maw={900}>
					{supportsDeliveryMethodChoice && (
						<Box>
							<Text fw={500} size='sm' mb={6}>
								Delivery Method
							</Text>
							<SegmentedControl
								value={deliveryMethod}
								onChange={setDeliveryMethod}
								data={deliveryMethodOptions}
							/>
						</Box>
					)}

					{showTabbedEditors ? (
						<Tabs
							value={activeEditorTab}
							onTabChange={(value) => setActiveEditorTab(value || 'email')}
						>
							<Tabs.List grow>
								<Tabs.Tab value='email'>Email</Tabs.Tab>
								<Tabs.Tab value='sms'>SMS</Tabs.Tab>
							</Tabs.List>

							<Tabs.Panel value='email' pt='md'>
								<Stack spacing='md'>
									{emailEditorFields}
									{emailPreview}
								</Stack>
							</Tabs.Panel>

							<Tabs.Panel value='sms' pt='md'>
								<Stack spacing='md'>
									{smsEditorFields}
									{smsPreview}
								</Stack>
							</Tabs.Panel>
						</Tabs>
					) : (
						<>
							{isEmailEnabled && emailEditorFields}
							{isSmsEnabled && smsEditorFields}
						</>
					)}

					<Group align='flex-start'>
						<Stack spacing={2}>
							<Button onClick={handleSaveTemplate} loading={saving}>
								Save Template
							</Button>
							{saveStatus === 'saving' && (
								<Text size='sm' c='blue'>
									Saving...
								</Text>
							)}
							{saveStatus === 'saved' && (
								<Text size='sm' c='dimmed'>
									Saved at{' '}
									{lastSavedAt &&
										lastSavedAt.toLocaleTimeString([], {
											hour: '2-digit',
											minute: '2-digit',
										})}
								</Text>
							)}
							{saveStatus === 'error' && (
								<Text size='sm' c='red'>
									Error saving
								</Text>
							)}
						</Stack>
						{showEmailTestButton && (
							<Button variant='outline' onClick={handleTestSend}>
								Send Test Email
							</Button>
						)}
						{showSmsTestButton && (
							<Button
								variant='outline'
								onClick={handleTestSendSms}
								loading={sendingTestSms}
							>
								Send Test SMS
							</Button>
						)}
					</Group>

					{!showTabbedEditors && (
						<>
							{isEmailEnabled && emailPreview}
							{isSmsEnabled && smsPreview}
						</>
					)}
				</Stack>
			)}

			<ImageCropperModal
				opened={cropModalOpened}
				onClose={() => setCropModalOpened(false)}
				title='Crop Header Image'
				sourceUrl={cropSourceUrl}
				targetWidth={HEADER_IMAGE_TARGET_WIDTH}
				targetHeight={HEADER_IMAGE_TARGET_HEIGHT}
				onApply={handleUploadCroppedHeaderImage}
				applyLabel='Use Cropped Header'
				processing={uploadingHeaderImage}
			/>
		</Box>
	)
}

export default EditExperienceStockTemplatePage
